import { Button, TextField, Typography } from "@mindlercare/ui-web";
import { FormEvent, useReducer, useRef, useState } from "react";
import { useAuth, useInternalAuth } from "../AuthenticationProvider";
import { LinkButton } from "../components/LinkButton";
import { useTranslation } from "../hooks/useTranslation";
import { useStateNavigator } from "../hooks/useStateNavigator";

const initialEmailAndPasswordFormState = {
  email: "",
  password: "",
  confirmCode: "",
};

type EmailAndPasswordFormState = typeof initialEmailAndPasswordFormState;

export const SignInWithEmailAndPassword = () => {
  const { t } = useTranslation();
  const { navigate } = useStateNavigator();
  const { market } = useInternalAuth();
  const [requireMFA, setRequireMFA] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // loginState cannot block a second submit: loginWithEmailAndPassword logs out
  // first, which resets it to "logged-out" while the sign-in is still running.
  // Every extra sign-in or resend sends the user another MFA SMS with a new code.
  const inFlight = useRef(false);
  const { loginState } = useAuth();

  const [{ email, password, confirmCode }, dispatch] = useReducer(
    (
      prev: EmailAndPasswordFormState,
      payload: Partial<EmailAndPasswordFormState>,
    ) => ({
      ...prev,
      ...payload,
    }),
    initialEmailAndPasswordFormState,
  );
  const {
    loginWithEmailAndPassword,
    confirmSignIn,
    setPendingStatus,
    resendMFA,
  } = useInternalAuth();

  const handleFormUpdate = (
    key: keyof EmailAndPasswordFormState,
    value: string,
  ) => {
    if (errorMessage.length > 0) {
      setErrorMessage("");
    }

    dispatch({ [key]: value });
  };

  const runOnce = async (action: () => Promise<void>) => {
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;
    setIsSubmitting(true);

    try {
      await action();
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const submit = async () => {
    setPendingStatus();

    if (!requireMFA) {
      try {
        const { success, nextStep } = await loginWithEmailAndPassword(
          email,
          password,
        );

        if (!success && nextStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
          setRequireMFA(true);
          return;
        }
      } catch (error: unknown) {
        let errorMessage = t("ERROR_UNKNOWN");

        if (error instanceof Error) {
          errorMessage = error.message;
        }

        return setErrorMessage(errorMessage);
      }
    } else {
      try {
        await confirmSignIn(confirmCode);
      } catch (error: unknown) {
        if (!(error instanceof Error)) {
          console.error(error);
          return setErrorMessage(t("ERROR_UNKNOWN"));
        }

        if (error.name === "CodeMismatchException") {
          return setErrorMessage(t("WRONG_SMS_CODE"));
        }

        if (error.name === "NotAuthorizedException") {
          return setErrorMessage(t("EXPIRED_SMS_CODE"));
        }

        console.error(error);
        return setErrorMessage(t("ERROR_UNKNOWN"));
      }
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    return runOnce(submit);
  };

  return (
    <form onSubmit={handleSubmit}>
      {!requireMFA ? (
        <>
          <Typography variant="h3">{t("LOGIN_TO_ACCOUNT")}</Typography>
          <div style={{ marginTop: "24px" }}>
            <TextField
              testId="login-email"
              placeholder={t("EMAIL_PLACEHOLDER")}
              label={t("EMAIL")}
              value={email}
              onChange={(e) => handleFormUpdate("email", e.target.value)}
              hideSensitiveData={true}
              error={!!errorMessage}
            />
            <div style={{ marginTop: "12px" }} />
            <TextField
              testId="login-password"
              placeholder={t("PASSWORD_PLACEHOLDER")}
              label={t("PASSWORD")}
              type="password"
              value={password}
              onChange={(e) => handleFormUpdate("password", e.target.value)}
              hideSensitiveData={true}
              error={errorMessage.length > 0}
              errorMessage={errorMessage}
            />
          </div>
          <div style={{ display: "flex", margin: "24px 0px" }}>
            <Typography variant="body-small" color="DARK_GREY">
              {t("FORGOT_YOUR_PASSWORD")}{" "}
              <LinkButton
                type="button"
                underline
                color="DARK_GREY"
                onClick={() => {
                  navigate({ view: "reset-password", market });
                }}
              >
                {t("RESET_PASSWORD")}
              </LinkButton>
            </Typography>
          </div>
          <Button
            testId="login-button"
            variant="primary"
            disabled={
              !email ||
              !password ||
              isSubmitting ||
              loginState === "login-in-progress"
            }
            type="submit"
          >
            {t("LOGIN")}
          </Button>
        </>
      ) : (
        <>
          <Typography variant="h3">{t("CONFIRM_SMS_CODE")}</Typography>
          <div style={{ margin: "24px 0px 0px 0px" }}>
            <TextField
              type="number"
              placeholder={t("CODE")}
              label={`${t("CONFIRMATION_CODE")}*`}
              value={confirmCode}
              onChange={(e) => handleFormUpdate("confirmCode", e.target.value)}
              hideSensitiveData={true}
              error={errorMessage.length > 0}
              errorMessage={errorMessage}
            />
          </div>
          <Button
            mt={8}
            variant="link-button"
            onClick={() =>
              runOnce(async () => {
                await resendMFA();
              })
            }
            disabled={isSubmitting}
            type="button"
          >
            {t("LOST_CODE")}
          </Button>
          <Button
            mt={24}
            variant="primary"
            disabled={!confirmCode || isSubmitting}
            type="submit"
          >
            {t("CONFIRM")}
          </Button>
        </>
      )}
    </form>
  );
};
